<script setup lang="ts">
import { onMounted, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { SortIdOption, SortOrderOption } from "@/../types/promos-types";

const { t } = useI18n({ useScope: "global" });

const sortButton = ref<Element>();
const isDropdownOpen = ref(false);

interface Props {
  id: SortIdOption;
  order: SortOrderOption;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "changeSort", id: SortIdOption, order: SortOrderOption): () => void;
}>();

const changeSort = (id: SortIdOption, order: SortOrderOption) => {
  if (id !== props.id || order !== props.order) {
    emit("changeSort", id, order);
  }
};

onMounted(() => {
  const sortMenu = document.querySelector(".vacv-dropdown-menu");
  if (sortMenu) {
    sortMenu.setAttribute("aria-activedescendant", sortSelected.value);
  }

  document.addEventListener("click", onDocumentClick);
});

const onDocumentClick = (e: MouseEvent) => {
  // Hide the dropdown when click outside the sort button
  if (
    sortButton.value &&
    e &&
    e.target instanceof Element &&
    !(e.target == sortButton.value || sortButton.value.contains(e.target))
  ) {
    isDropdownOpen.value = false;
  }
};

const onSortButtonClick = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const sortSelected = computed(() => {
  switch (props.id) {
    case "price":
      return props.order === "asc" ? "option-1" : "option-2";
    case "star-rating":
      return props.order === "asc" ? "option-4" : "option-5";
    case "departure-date":
      return "option-3";
    default:
      return "option-1";
  }
});

const sortByText = computed(() => {
  switch (props.id) {
    case "price":
      return props.order === "asc"
        ? t("plp.sorting.lowest-price")
        : t("plp.sorting.highest-price");
    case "star-rating":
      return props.order === "asc"
        ? t("plp.sorting.lowest-star-rating")
        : t("plp.sorting.highest-star-rating");
    case "departure-date":
      return t("plp.sorting.earliest-departure-date");
    default:
      return "";
  }
});

// Expose ref and methods for unit tests
defineExpose({ isDropdownOpen, onDocumentClick });
</script>

<template>
  <div class="sortby-wrapper">
    <div class="sortby">
      <div class="desktop-only vacv-label-sm">
        {{ $t("plp.sorting.sort-description") }}
      </div>
      <div
        class="vacv-components-dropdown-wrapper"
        :class="[{ open: isDropdownOpen }, '']"
      >
        <button
          id="plp-sortby"
          ref="sortButton"
          class="vacv-btn-outline btn-sort-order"
          @click="onSortButtonClick"
          aria-haspopup="true"
          :aria-expanded="isDropdownOpen ? true : false"
        >
          <span class="mobile-only dropdown-btn-text">
            <span class="icon-wrapper">
              <i class="fa-solid fa-chevron-up" aria-hidden="true"></i>
              <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
            </span>
            <span class="vacv-label-sm">{{
              $t("plp.sorting.sort-description")
            }}</span>
          </span>

          <span class="desktop-only dropdown-btn-text">
            {{ sortByText }}
            <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
          </span>
        </button>

        <ul
          class="vacv-dropdown-menu"
          role="listbox"
          aria-labelledby="plp-sortby"
          aria-activedescendant="option-1"
          id="dropdown-menu-dropdown-sort-by"
        >
          <li
            id="option-1"
            role="option"
            :class="sortSelected == 'option-1' ? 'selected' : ''"
            :aria-selected="sortSelected == 'option-1' ? true : false"
            @click="changeSort('price', 'asc')"
          >
            <button
              class="dropdown-option"
              id="lowest-price-option"
              type="button"
            >
              <i class="fa-solid fa-check" aria-hidden="true"></i>
              {{ $t("plp.sorting.lowest-price") }}
            </button>
          </li>
          <li
            id="option-2"
            role="option"
            :class="sortSelected == 'option-2' ? 'selected' : ''"
            :aria-selected="sortSelected == 'option-2' ? true : false"
            @click="changeSort('price', 'desc')"
          >
            <button
              class="dropdown-option"
              id="highest-price-option"
              type="button"
            >
              <i class="fa-solid fa-check" aria-hidden="true"></i>
              {{ $t("plp.sorting.highest-price") }}
            </button>
          </li>
          <li
            id="option-3"
            role="option"
            :class="sortSelected == 'option-3' ? 'selected' : ''"
            :aria-selected="sortSelected == 'option-3' ? true : false"
            @click="changeSort('departure-date', 'asc')"
          >
            <button
              class="dropdown-option"
              id="earliest-departure-date-option"
              type="button"
            >
              <i class="fa-solid fa-check" aria-hidden="true"></i>
              {{ $t("plp.sorting.earliest-departure-date") }}
            </button>
          </li>
          <li
            id="option-4"
            role="option"
            :class="sortSelected == 'option-4' ? 'selected' : ''"
            :aria-selected="sortSelected == 'option-4' ? true : false"
            @click="changeSort('star-rating', 'asc')"
          >
            <button
              class="dropdown-option"
              id="lowest-star-rating-option"
              type="button"
            >
              <i class="fa-solid fa-check" aria-hidden="true"></i>
              {{ $t("plp.sorting.lowest-star-rating") }}
            </button>
          </li>
          <li
            id="option-5"
            role="option"
            :class="sortSelected == 'option-5' ? 'selected' : ''"
            :aria-selected="sortSelected == 'option-5' ? true : false"
            @click="changeSort('star-rating', 'desc')"
          >
            <button
              class="dropdown-option"
              id="highest-star-rating-option"
              type="button"
            >
              <i class="fa-solid fa-check" aria-hidden="true"></i>
              {{ $t("plp.sorting.highest-star-rating") }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <!-- // . sortby-wrapper -->
</template>

<style scoped lang="scss">
@use "vacv-components/themes/acv-default/default-includes" as *;

.sortby-wrapper {
  clear: both;
  margin-top: 20px;
  margin-bottom: 20px;
  margin-left: auto;

  .sortby {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .vacv-btn-outline {
      padding: 0 15px !important;
      .dropdown-btn-text.desktop-only {
        .fa-chevron-down {
          margin-left: 30px;
        }
      }
    }
  }
}
</style>
